import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Tooltip } from "recharts";
import { useChartLock } from "./ChartLockContext";
import { CROSSHAIR_CURSOR } from "./chartConstants";

export function useRechartsLegendSync(
    data: Record<string, any>[],
    options?: {
        xKey?: string;       // X軸キー (default: "index")
        resetOnData?: boolean; // データ差し替え時に凡例を 0 に戻す (default: true)
    }
) {
    const { xKey = "index", resetOnData = true } = options ?? {};

    // 共有 x 軸の固定状態（provider 不在なら常に非固定＝従来挙動）。
    const { lockedValue, setLockedValue, lastHoveredValueRef } = useChartLock();

    // X軸ラベル -> data 配列インデックス
    const labelToIndex = useMemo(() => {
        const m = new Map<any, number>();
        data.forEach((row, i) => m.set(row?.[xKey], i));
        return m;
    }, [data, xKey]);

    // 外部凡例の index を更新するための ref（親から凡例へ渡す）
    const setIndexRef = useRef<(i: number) => void>(() => { });

    // rAF スロットリング（親は再レンダしない）
    const rafId = useRef<number | null>(null);
    const pending = useRef<number | null>(null);

    const setLegendIndex = useCallback((i: number) => {
        if (i < 0 || i >= data.length) return;
        pending.current = i;
        if (rafId.current == null) {
            rafId.current = requestAnimationFrame(() => {
                if (pending.current != null) setIndexRef.current(pending.current);
                rafId.current = null;
            });
        }
    }, [data.length]);

    useEffect(() => {
        return () => {
            if (rafId.current != null) cancelAnimationFrame(rafId.current);
        };
    }, []);

    // データ差し替え時の初期化。固定中は固定 index へ、非固定は 0 に戻す。
    useEffect(() => {
        if (!resetOnData) return;
        pending.current = null;
        if (lockedValue != null && labelToIndex.has(lockedValue)) {
            setIndexRef.current(labelToIndex.get(lockedValue)!);
        } else {
            setIndexRef.current(0);
        }
    }, [data, resetOnData, lockedValue, labelToIndex]);

    // 固定値が変わったら凡例をその位置へ即時に移す。
    useEffect(() => {
        if (lockedValue != null && labelToIndex.has(lockedValue)) {
            const i = labelToIndex.get(lockedValue)!;
            pending.current = i;
            setIndexRef.current(i);
        }
    }, [lockedValue, labelToIndex]);

    // 自チャートの hover（activeTooltipIndex）を凡例へ反映。
    const onMouseMove = useCallback((state: any) => {
        const i = state?.activeTooltipIndex;
        if (typeof i !== "number") return;
        // 固定中でも直近ホバー位置は記録する（トグルでの固定位置に使う）。
        const v = data[i]?.[xKey];
        if (typeof v === "number") lastHoveredValueRef.current = v;
        if (lockedValue != null) return; // 固定中はホバー追従しない
        setLegendIndex(i);
    }, [setLegendIndex, lockedValue, data, xKey, lastHoveredValueRef]);

    // マウスアウト時の挙動。固定中は維持する。
    const onMouseLeave = useCallback(() => {
        if (lockedValue != null) return;
        setLegendIndex(pending.current ?? 0);
    }, [setLegendIndex, lockedValue]);

    // クリックで固定/解除（クリック位置の共有 x 値で固定。固定中の再クリックで解除）。
    const onClick = useCallback((state: any) => {
        const raw = state?.activeLabel;
        const num = typeof raw === "number" ? raw : Number(raw);
        if (Number.isNaN(num)) return;
        setLockedValue(lockedValue != null ? null : num);
    }, [lockedValue, setLockedValue]);

    // 同期イベント拾い用の非表示 Tooltip
    const tooltipContent = useCallback((props: any) => {
        const { active, label } = props; // label は XAxis.dataKey
        if (active) {
            if (typeof label === "number") lastHoveredValueRef.current = label;
            if (lockedValue != null) return null; // 固定中は他チャート同期も無視
            const i = labelToIndex.get(label);
            if (typeof i === "number") setLegendIndex(i);
        }
        return null;
    }, [labelToIndex, setLegendIndex, lockedValue, lastHoveredValueRef]);

    // content は非表示のまま cursor だけ可視化＝ホバー時のクロスヘア（縦線）。
    // syncId により他チャートにも同 x でカーソルが出る（共有クロスヘア）。
    const HiddenSyncTooltip = useMemo(
        () => <Tooltip wrapperStyle={{ display: "none" }} content={tooltipContent} cursor={CROSSHAIR_CURSOR} />,
        [tooltipContent]
    );

    return {
        setIndexRef,
        HiddenSyncTooltip,
        handlers: { onMouseMove, onMouseLeave, onClick },
        // 固定中の共有 x 値。viewer はこれを使って ReferenceLine（ガイド線）を描く。
        lockedValue,
    };
}
